import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Hammer, TrendingDown, TrendingUp, Wallet, Landmark, ClipboardList, Coins, ReceiptText,
  FolderKanban, Rocket, DoorOpen, Activity, AlertTriangle, Inbox, CircleDot,
  ArrowRight, Gauge, Trophy, Timer, ChevronRight,
} from 'lucide-react';
import api from '../lib/api';
import { fmtMoneyLabel, fmtCompactMoney } from '../lib/format';
import { filsToAed } from '../lib/engine';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

// P7 — Senior-management COCKPIT. Pure presentation of existing aggregates
// (GET /dashboard/cockpit); every figure drills into the relevant module.

const pctOf = (part, whole) => {
  const w = filsToAed(whole || 0);
  if (!w) return 0;
  return Math.max(0, Math.min(100, (filsToAed(part || 0) / w) * 100));
};

const SectionHeader = ({ icon: Icon, title, hint, onDrill, drillLabel }) => (
  <div className="mb-3 flex items-end justify-between">
    <div className="flex items-center gap-2">
      <span className="grid h-7 w-7 place-items-center rounded-lg bg-[hsl(var(--muted)/0.6)] text-[hsl(var(--muted-foreground))]"><Icon size={15} /></span>
      <h2 className="font-display text-base font-semibold tracking-tight md:text-lg">{title}</h2>
      {hint && <span className="hidden text-xs text-muted-foreground sm:inline">· {hint}</span>}
    </div>
    {onDrill && (
      <button onClick={onDrill} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-[hsl(var(--brand))] transition-colors hover:bg-[hsl(var(--brand)/0.08)]">
        {drillLabel || 'View'} <ChevronRight size={13} />
      </button>
    )}
  </div>
);

// A compact, clickable metric tile.
const Tile = ({ icon: Icon, label, value, sub, tone = 'muted', onClick, testid, danger, positive }) => (
  <button
    type="button" onClick={onClick} data-testid={testid}
    className={cn(
      'erak-card group flex flex-col gap-1 p-4 text-left transition-all duration-200',
      onClick && 'hover:-translate-y-0.5 hover:shadow-lg focus-visible:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand)/0.5)]',
    )}
  >
    <div className="flex items-center justify-between">
      <span className={cn('grid h-8 w-8 place-items-center rounded-lg', `bg-[hsl(var(--${tone})/0.12)] text-[hsl(var(--${tone}))]`)}><Icon size={16} /></span>
      {onClick && <ArrowRight size={14} className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />}
    </div>
    <div className="mt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={cn('font-num text-lg font-bold leading-tight tabular-nums', danger && 'text-[hsl(var(--danger))]', positive && 'text-[hsl(var(--success))]')} data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </button>
);

// Labelled horizontal proportion bar (share of contract value).
const ProportionRow = ({ icon: Icon, label, value, pct, tone, cur, onClick }) => (
  <button type="button" onClick={onClick}
    className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-[hsl(var(--muted)/0.4)]">
    <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', `bg-[hsl(var(--${tone})/0.12)] text-[hsl(var(--${tone}))]`)}><Icon size={15} /></span>
    <div className="min-w-0 flex-1">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="truncate text-xs text-muted-foreground">{label}</span>
        <span className="font-num text-sm font-semibold tabular-nums">{fmtMoneyLabel(value, cur, 0)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[hsl(var(--muted)/0.7)]">
        <div className={cn('h-full rounded-full transition-[width] duration-500', `bg-[hsl(var(--${tone}))]`)} style={{ width: `${pct}%` }} />
      </div>
    </div>
    <span className="w-12 shrink-0 text-right font-num text-[11px] tabular-nums text-muted-foreground">{pct.toFixed(0)}%</span>
  </button>
);

// Small SVG progress ring (win rate).
const Ring = ({ pct, tone = 'success', size = 68 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`hsl(var(--${tone}))`} strokeWidth="6"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  );
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard/cockpit').then((r) => setD(r.data)).catch(() => setErr(true));
  }, []);

  if (err) {
    return (
      <div className="p-8">
        <div className="erak-card p-8 text-center text-sm text-muted-foreground" data-testid="cockpit-error">
          Unable to load the management cockpit right now. Please refresh.
        </div>
      </div>
    );
  }

  if (!d) {
    return (
      <div className="space-y-6 p-4 md:p-6 lg:p-8">
        <div className="grid gap-4 md:grid-cols-2">{['a', 'b'].map((k) => <Skeleton key={k} className="h-44 rounded-2xl" />)}</div>
        <Skeleton className="h-16 rounded-2xl" />
        <div className="grid gap-4 md:grid-cols-4">{['a', 'b', 'c', 'd'].map((k) => <Skeleton key={k} className="h-28 rounded-2xl" />)}</div>
      </div>
    );
  }

  const cur = d.currency || 'AED';
  const { hero, pipeline, financial, operational, alerts } = d;
  const money = (f, dec = 0) => fmtMoneyLabel(f, cur, dec);
  const signed = (f) => `${f > 0 ? '+' : f < 0 ? '−' : ''}${fmtMoneyLabel(Math.abs(f), cur, 0)}`;
  const go = (path) => () => navigate(path);

  // WIP proportion bar (magnitude of under vs over billed).
  const wipAbsUnder = Math.abs(filsToAed(hero.wipUnderBilled));
  const wipAbsOver = Math.abs(filsToAed(hero.wipOverBilled));
  const wipTotal = wipAbsUnder + wipAbsOver || 1;
  const underShare = (wipAbsUnder / wipTotal) * 100;

  // Margin gauge scale 0–40%.
  const MARGIN_SCALE = 40;
  const budgetedMark = Math.max(0, Math.min(100, (hero.budgetedMarginPct / MARGIN_SCALE) * 100));
  const projectedMark = hero.projectedMarginPct != null ? Math.max(0, Math.min(100, (hero.projectedMarginPct / MARGIN_SCALE) * 100)) : null;

  const alertItems = [
    { key: 'approvals', icon: Inbox, label: 'Approvals pending', count: alerts.approvalsPending, tone: 'brand', path: '/approvals' },
    { key: 'erosion', icon: TrendingDown, label: 'Margin erosion', count: alerts.marginErosionProjects, tone: 'danger', path: '/costing?flag=erosion' },
    { key: 'overbilled', icon: ReceiptText, label: 'Over-billed', count: alerts.overBilledProjects, tone: 'danger', path: '/costing?flag=over_billed' },
    { key: 'awaiting', icon: Rocket, label: 'Awaiting opening', count: alerts.awaitingOpening, tone: 'warning', path: '/costing?flag=awarded' },
    { key: 'overdue', icon: Timer, label: 'Overdue', count: alerts.overdueProjects, tone: 'warning', path: '/costing' },
  ];
  const totalAlerts = alertItems.reduce((s, a) => s + (a.count || 0), 0);

  return (
    <div className="space-y-8 p-4 md:p-6 lg:p-8" data-testid="cockpit">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Management Cockpit</h1>
          <p className="text-sm text-muted-foreground">Portfolio health at a glance — cash to collect, margin, pipeline and what needs you.</p>
        </div>
        {totalAlerts > 0 && (
          <button onClick={go('/approvals')} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--brand)/0.3)] bg-[hsl(var(--brand)/0.06)] px-3 py-2 text-xs font-medium text-[hsl(var(--brand))] transition-colors hover:bg-[hsl(var(--brand)/0.12)]">
            <AlertTriangle size={14} />{totalAlerts} item(s) need attention
          </button>
        )}
      </div>

      {/* HERO ROW — two co-heroes */}
      <div className="grid gap-4 md:grid-cols-2 md:gap-6">
        {/* WIP under-billed = cash to collect */}
        <motion.button
          type="button" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          onClick={go('/costing?flag=under_billed')} data-testid="hero-wip"
          className="erak-card group relative overflow-hidden p-6 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl focus-visible:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand)/0.5)]"
        >
          <div className="absolute -right-8 -top-8 h-44 w-44 rounded-full bg-[hsl(var(--teal)/0.12)] blur-3xl" aria-hidden />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-[hsl(var(--teal)/0.14)] text-[hsl(var(--teal))]"><Hammer size={16} /></span>
                WIP · cash to collect
              </div>
              <ArrowRight size={16} className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <div className="mt-4 font-num text-4xl font-bold tabular-nums text-[hsl(var(--teal))] md:text-5xl" data-testid="hero-wip-value">
              {money(hero.wipUnderBilled)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">certified but not yet invoiced (under-billed)</div>

            {/* proportion bar: under vs over billed */}
            <div className="mt-5 flex h-2.5 overflow-hidden rounded-full bg-[hsl(var(--muted)/0.7)]">
              <div className="h-full bg-[hsl(var(--teal))]" style={{ width: `${underShare}%` }} />
              <div className="h-full bg-[hsl(var(--danger))]" style={{ width: `${100 - underShare}%` }} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-testid="hero-wip-overbilled">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--danger))]" />Over-billed <span className="font-num font-semibold text-[hsl(var(--danger))]">{money(hero.wipOverBilled)}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--teal))]" />Net WIP <span className="font-num font-semibold text-foreground">{signed(hero.wipNet)}</span>
              </span>
            </div>
          </div>
        </motion.button>

        {/* Margin erosion */}
        <motion.button
          type="button" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          onClick={go('/costing?flag=erosion')} data-testid="hero-erosion"
          className="erak-card group relative overflow-hidden p-6 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl focus-visible:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand)/0.5)]"
        >
          <div className={cn('absolute -right-8 -top-8 h-44 w-44 rounded-full blur-3xl', hero.erosionUnfavourable ? 'bg-[hsl(var(--danger)/0.14)]' : 'bg-[hsl(var(--success)/0.12)]')} aria-hidden />
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <span className={cn('grid h-8 w-8 place-items-center rounded-lg', hero.erosionUnfavourable ? 'bg-[hsl(var(--danger)/0.14)] text-[hsl(var(--danger))]' : 'bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))]')}>
                  {hero.erosionUnfavourable ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                </span>
                Margin erosion
              </div>
              <ArrowRight size={16} className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <div className={cn('mt-4 font-num text-4xl font-bold tabular-nums md:text-5xl', hero.erosionUnfavourable ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--success))]')} data-testid="hero-erosion-value">
              {hero.marginErosion > 0 ? '−' : hero.marginErosion < 0 ? '+' : ''}{fmtMoneyLabel(Math.abs(hero.marginErosion), cur, 0)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{hero.erosionUnfavourable ? 'projected margin below budget' : 'actuals within budget'}</div>

            {/* budgeted vs projected margin gauge (0–40%) */}
            <div className="relative mt-6 h-2.5 rounded-full bg-[hsl(var(--muted)/0.7)]">
              <div className="absolute inset-y-0 left-0 rounded-full bg-[hsl(var(--brand)/0.35)]" style={{ width: `${budgetedMark}%` }} />
              {projectedMark != null && (
                <div className="absolute -top-1 h-4.5 w-1 -translate-x-1/2 rounded-full bg-[hsl(var(--foreground))]" style={{ left: `${projectedMark}%`, height: 18, top: -3 }} title="Projected" />
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--brand)/0.6)]" />Budgeted <span className="font-num font-semibold text-foreground" data-testid="hero-budgeted-margin-pct">{hero.budgetedMarginPct}%</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--foreground))]" />Projected <span className="font-num font-semibold text-foreground" data-testid="hero-projected-margin-pct">{hero.projectedMarginPct != null ? `${hero.projectedMarginPct}%` : 'POC n/a'}</span>
              </span>
            </div>
          </div>
        </motion.button>
      </div>

      {/* ALERTS STRIP — what needs me */}
      <div className="erak-card p-4" data-testid="cockpit-alerts">
        <div className="mb-3 flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]"><AlertTriangle size={15} /></span>
          <h2 className="font-display text-base font-semibold tracking-tight">What needs me</h2>
          <span className="text-xs text-muted-foreground">{totalAlerts === 0 ? '· all clear' : `· ${totalAlerts} item(s)`}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {alertItems.map((a) => {
            const active = (a.count || 0) > 0;
            return (
              <button
                key={a.key} onClick={go(a.path)} data-testid={`alert-${a.key}`}
                className={cn(
                  'flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-all duration-200',
                  active
                    ? `border-[hsl(var(--${a.tone})/0.4)] bg-[hsl(var(--${a.tone})/0.08)] hover:-translate-y-0.5 hover:bg-[hsl(var(--${a.tone})/0.14)]`
                    : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50',
                )}
              >
                <a.icon size={15} className={active ? `text-[hsl(var(--${a.tone}))]` : ''} />
                <span className={active ? 'font-medium' : ''}>{a.label}</span>
                <span className={cn('grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-xs font-bold tabular-nums',
                  active ? `bg-[hsl(var(--${a.tone}))] text-white` : 'bg-muted text-muted-foreground')} data-testid={`alert-${a.key}-count`}>
                  {a.count || 0}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* FINANCIAL — position panel (visual) + detail tiles */}
      <section data-testid="cockpit-financial">
        <SectionHeader icon={Landmark} title="Financial" hint="portfolio totals" onDrill={go('/costing')} drillLabel="Cost Centre Portfolio" />
        <div className="grid gap-4 lg:grid-cols-12">
          {/* Position panel */}
          <div className="erak-card p-5 lg:col-span-5" data-testid="financial-position">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Contract Value ({cur})</div>
                <div className="font-num text-2xl font-bold tabular-nums" data-testid="fin-contract-value-value">{money(financial.contractValue)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Budgeted margin</div>
                <div className="font-num text-xl font-bold tabular-nums text-[hsl(var(--success))]">{financial.budgetedMarginPct}%</div>
              </div>
            </div>
            <div className="mt-4 space-y-1">
              <ProportionRow icon={Wallet} label="Budget" value={financial.budget} pct={pctOf(financial.budget, financial.contractValue)} tone="teal" cur={cur} onClick={go('/costing')} />
              <ProportionRow icon={ClipboardList} label="Committed" value={financial.committed} pct={pctOf(financial.committed, financial.contractValue)} tone="copper" cur={cur} onClick={go('/costing')} />
              <ProportionRow icon={Coins} label="Actual (SAP)" value={financial.actual} pct={pctOf(financial.actual, financial.contractValue)} tone="warning" cur={cur} onClick={go('/costing')} />
              <ProportionRow icon={ReceiptText} label="Cumulative Invoiced" value={financial.invoiced} pct={pctOf(financial.invoiced, financial.contractValue)} tone="brand" cur={cur} onClick={go('/costing')} />
            </div>
          </div>

          {/* Detail tiles */}
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-2">
            <Tile testid="fin-contract-value" icon={Landmark} tone="brand" label={`Contract Value (${cur})`} value={money(financial.contractValue)} sub={fmtCompactMoney(financial.contractValue, cur)} onClick={go('/costing')} />
            <Tile testid="fin-budget" icon={Wallet} tone="teal" label={`Budget (${cur})`} value={money(financial.budget)} sub={fmtCompactMoney(financial.budget, cur)} onClick={go('/costing')} />
            <Tile testid="fin-committed" icon={ClipboardList} tone="copper" label={`Committed (${cur})`} value={money(financial.committed)} sub={fmtCompactMoney(financial.committed, cur)} onClick={go('/costing')} />
            <Tile testid="fin-actual" icon={Coins} tone="warning" label={`Actual (SAP) (${cur})`} value={money(financial.actual)} sub={fmtCompactMoney(financial.actual, cur)} onClick={go('/costing')} />
            <Tile testid="fin-invoiced" icon={ReceiptText} tone="teal" label={`Cumulative Invoiced (${cur})`} value={money(financial.invoiced)} sub={fmtCompactMoney(financial.invoiced, cur)} onClick={go('/costing')} />
            <Tile testid="fin-margin" icon={Gauge} tone="success" label="Margin % (budgeted / projected)"
              value={`${financial.budgetedMarginPct}% / ${financial.projectedMarginPct != null ? `${financial.projectedMarginPct}%` : 'n/a'}`}
              sub="budgeted vs projected" onClick={go('/costing')} />
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section data-testid="cockpit-pipeline">
        <SectionHeader icon={Trophy} title="Pipeline" hint="tenders out for bid" onDrill={go('/projects')} drillLabel="Tenders" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Tile testid="pipeline-live" icon={FolderKanban} tone="brand" label="Tenders Live" value={pipeline.tendersLive} sub="in progress" onClick={go('/projects')} />
          <Tile testid="pipeline-value-out" icon={Wallet} tone="teal" label={`Value Out for Bid (${cur})`} value={money(pipeline.valueOut)} sub={fmtCompactMoney(pipeline.valueOut, cur)} onClick={go('/projects')} />
          {/* Win-rate ring tile */}
          <button type="button" onClick={go('/projects')} data-testid="pipeline-win-rate"
            className="erak-card group flex items-center gap-4 p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand)/0.5)]">
            <div className="relative grid place-items-center">
              <Ring pct={pipeline.winRate} tone="success" />
              <span className="absolute font-num text-sm font-bold tabular-nums" data-testid="pipeline-win-rate-value">{pipeline.winRate}%</span>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Win Rate</div>
              <div className="font-num text-lg font-bold">{pipeline.decided} decided</div>
              <div className="text-[11px] text-muted-foreground">won vs lost</div>
            </div>
          </button>
          <Tile testid="pipeline-value-won" icon={Trophy} tone="copper" label={`Value Won (${cur})`} value={money(pipeline.valueWon)} sub={fmtCompactMoney(pipeline.valueWon, cur)} onClick={go('/costing')} />
        </div>
      </section>

      {/* OPERATIONAL */}
      <section data-testid="cockpit-operational">
        <SectionHeader icon={Activity} title="Operational" hint="cost centre delivery" onDrill={go('/costing')} drillLabel="Cost Centres" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <Tile testid="ops-awarded" icon={Rocket} tone="warning" label="Awarded (awaiting opening)" value={operational.awarded} sub="won, not opened" onClick={go('/costing?flag=awarded')} />
          <Tile testid="ops-open" icon={DoorOpen} tone="teal" label="Open Cost Centres" value={operational.open} sub="in delivery" onClick={go('/costing')} />
          <Tile testid="ops-active" icon={FolderKanban} tone="brand" label="Active Projects" value={operational.activeProjects} sub="total won" onClick={go('/costing')} />
          <Tile testid="ops-over-budget" icon={AlertTriangle} tone="danger" label="Over Budget" value={operational.overBudget} danger={operational.overBudget > 0} sub="spend > budget" onClick={go('/costing?health=over_budget')} />
          <Tile testid="ops-at-risk" icon={CircleDot} tone="warning" label="At Risk" value={operational.atRisk} sub="80–100% spent" onClick={go('/costing?health=at_risk')} />
        </div>
      </section>
    </div>
  );
}
